import type { AskPageQuestion, Plan } from "@/client";
import type { QuestionStep } from "~/modules/shared/utils/questionActivity";

/** Fields from {@link PublicAskPagePageVars} / AskGvt1Controller shell merge. */
type ConsumerShellVars = {
	isSignedIn?: boolean;
	appEmbedded?: boolean;
	/** App Store listing or `askgvt://` deep link. Same as `SMALL_SCREEN_GATE_APP_URL`. */
	getTheAppUrl?: string | null;
	user_handle?: string | null;
	user_avatar?: string | null;
	currentlyEntitledCreatorCount?: number;
	/** Catalogue id the signed-in account is on. `free` when there is no live row. */
	currentPlanId?: string | null;
	/**
	 * Plans priced in each catalogue currency. Present on /plans.
	 * Use {@link billingCurrency} when set; otherwise the browser locale.
	 */
	plans?: Partial<Record<string, Plan[]>>;
	/** Set once the account's billing currency is locked. Null while it is still open. */
	billingCurrency?: string | null;
	/** Discovery category slugs this user follows. Empty for a guest. */
	followedCategoryIds?: string[];
	pool_id?: string | null;
	category_id?: string | null;
	category?: { id: string; name: string; icon: string } | null;
};

/** Legacy {@link PageController::renderPageVars} keys still on authenticated AskGVT pages. */
type LegacyPageControllerVars = {
	userAvatar?: string | null;
	userEmail?: string;
	influencerHandle?: string | null;
	poolId?: string;
	poolName?: string;
	boardId?: string;
	userId?: number;
};

export type SidebarAskPageInfluencer = {
	id: string;
	handle: string;
	display_name: string | null;
	avatar_url: string | null;
};

/** Currently-entitled AskPage subscriptions, newest first, sliced to 5 for the sidebar. */
export type SidebarSubscription = {
	id: string;
	type: string;
	influencer: SidebarAskPageInfluencer;
};

export type PageVars = ConsumerShellVars &
	LegacyPageControllerVars & {
		userName?: string;
		recentQuestions?: Array<
			AskPageQuestion & { progress?: QuestionStep | null }
		>;
		subscriptions?: SidebarSubscription[];
	};

const readRawPageVars = (): PageVars =>
	(globalThis as unknown as { pageVars: PageVars }).pageVars;

/** Normalizes consumer shell + legacy PageController pageVars for sidebar and layout chrome. */
export const pageVars = (): PageVars & {
	isSignedIn: boolean;
	followedCategoryIds: string[];
} => {
	const raw = readRawPageVars();
	return {
		...raw,
		isSignedIn: raw.isSignedIn ?? Boolean(raw.userName),
		appEmbedded: raw.appEmbedded ?? false,
		getTheAppUrl: raw.getTheAppUrl || null,
		user_handle: raw.user_handle ?? raw.influencerHandle ?? null,
		user_avatar: raw.user_avatar ?? raw.userAvatar ?? null,
		pool_id: raw.pool_id ?? raw.poolId ?? null,
		subscriptions: raw.subscriptions ?? [],
		recentQuestions: raw.recentQuestions ?? [],
		followedCategoryIds: raw.followedCategoryIds ?? [],
	};
};
